import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../../auth/AuthProvider';
import Header from '../../components/Header';
import KpiCard from '../../components/KpiCard';
import useUrlState from '../../hooks/useUrlState';
import { colors, radius, shadows, spacing } from '../../styles/theme';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const getAuthHeaders = (extra = {}) => {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}`, ...extra } : extra;
};

const cardStyle = {
  background: colors.surface,
  borderRadius: radius.lg,
  boxShadow: shadows.sm,
  border: `1px solid ${colors.border}`,
};
const inputStyle = {
  width: '100%',
  padding: '10px 12px',
  border: `1px solid ${colors.border}`,
  borderRadius: radius.sm,
  fontSize: '14px',
  boxSizing: 'border-box',
  background: colors.surface,
  color: colors.textPrimary,
};
const ghostBtn = {
  padding: '10px 18px',
  background: colors.surface,
  color: colors.textSecondary,
  border: `1px solid ${colors.border}`,
  borderRadius: radius.sm,
  cursor: 'pointer',
  fontWeight: 600,
  fontSize: '14px',
};

const miktarFmt = (n) => {
  const num = Number(n);
  if (!Number.isFinite(num)) return '0';
  return String(+num.toFixed(2));
};

// Üretim kaydındaki tarih alanını güvenli biçimde bulur.
// Backend sürümüne göre tarih alanının adı değişebildiği için olası alanlar desteklenir.
const getRunDate = (run) => {
  const rawDate =
    run.uretim_tarihi ||
    run.tarih ||
    run.baslangic_tarihi ||
    run.tamamlanma_tarihi ||
    run.created_at ||
    run.updated_at;

  if (!rawDate) return '';
  return String(rawDate).slice(0, 10);
};

const UretimOzetPage = () => {
  useAuth();

  // ── Filtreler (URL'de korunur) ──
  const [lokasyonFiltre, setLokasyonFiltre] = useUrlState('ozet_lokasyon', '');
  const [tarihBaslangicFiltre, setTarihBaslangicFiltre] = useUrlState('ozet_tarih_baslangic', '');
  const [tarihBitisFiltre, setTarihBitisFiltre] = useUrlState('ozet_tarih_bitis', '');
  const [sadeceTamamlanan, setSadeceTamamlanan] = useUrlState('ozet_sadece_tamamlanan', '1');

  const [lokasyonlar, setLokasyonlar] = useState([]);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);

  // ── Lokasyon listesi (filtre için) ──
  useEffect(() => {
    fetch(`${API_URL}/api/lokasyonlar`, { headers: getAuthHeaders() })
      .then(r => r.json())
      .then(d => setLokasyonlar(Array.isArray(d) ? d : []))
      .catch(() => setLokasyonlar([]));
  }, []);

  // ── Üretim kayıtlarını çek (backend'de tarih aralığı + lokasyon filtreli) ──
  const refreshRuns = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (lokasyonFiltre) params.set('lokasyon_id', lokasyonFiltre);
      // Tarih filtresi aşağıda sayfa tarafında uygulanıyor.
      // Böylece backend bu parametreleri desteklemese bile tarih aralığı çalışır.

      const qs = params.toString() ? `?${params.toString()}` : '';
      const rs = await fetch(`${API_URL}/api/production/recipe-runs${qs}`, { headers: getAuthHeaders() }).then(r => r.json());
      setRuns(Array.isArray(rs) ? rs : []);
    } catch {
      setRuns([]);
    }
    setLoading(false);
  }, [lokasyonFiltre]);

  useEffect(() => {
    refreshRuns();
  }, [refreshRuns]);

  const filtreAktif = Boolean(lokasyonFiltre || tarihBaslangicFiltre || tarihBitisFiltre);
  const clearFiltreler = () => {
    setLokasyonFiltre('');
    setTarihBaslangicFiltre('');
    setTarihBitisFiltre('');
  };

  // ── Tarih aralığını sayfa tarafında uygula ──
  const tarihFiltreliRuns = useMemo(() => {
    return runs.filter((run) => {
      const runDate = getRunDate(run);

      // Kaydın tarihi yoksa, tarih filtresi aktifken listeye dahil etme.
      if ((tarihBaslangicFiltre || tarihBitisFiltre) && !runDate) return false;
      if (tarihBaslangicFiltre && runDate < tarihBaslangicFiltre) return false;
      if (tarihBitisFiltre && runDate > tarihBitisFiltre) return false;

      return true;
    });
  }, [runs, tarihBaslangicFiltre, tarihBitisFiltre]);

  // ── Aynı isimdeki ürünlerin (reçetelerin) miktarlarını topla ──
  const ozet = useMemo(() => {
    const dahilRuns = sadeceTamamlanan === '1'
      ? tarihFiltreliRuns.filter(r => r.durum === 'TAMAMLANDI')
      : tarihFiltreliRuns;

    const gruplar = new Map();
    dahilRuns.forEach((r) => {
      const ad = r.recipe_ad || `Reçete #${r.recipe_id}`;
      const birim = r.birim || '';
      const anahtar = `${ad}__${birim}`;

      if (!gruplar.has(anahtar)) {
        gruplar.set(anahtar, {
          ad,
          birim,
          toplamMiktar: 0,
          kayitSayisi: 0,
        });
      }
      const grup = gruplar.get(anahtar);
      grup.toplamMiktar += Number(r.uretim_miktari) || 0;
      grup.kayitSayisi += 1;
    });

    return Array.from(gruplar.values()).sort((a, b) => b.toplamMiktar - a.toplamMiktar);
  }, [tarihFiltreliRuns, sadeceTamamlanan]);

  const toplamKayit = ozet.reduce((s, g) => s + g.kayitSayisi, 0);
  const urunCesidi = ozet.length;

  return (
    <div>
      <Header title="Üretim Özeti" />
      <div style={{ padding: spacing.lg, background: colors.background, minHeight: 'calc(100vh - 60px)' }}>

        {/* KPI Kartları */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: spacing.base, marginBottom: spacing.lg }}>
          <KpiCard title="Ürün Çeşidi" value={urunCesidi} icon="🏭" color={colors.primary}
            subtitle="Aynı isimde gruplanmış" />
          <KpiCard title="Toplam Kayıt" value={toplamKayit} icon="📋" color={colors.info}
            subtitle="Toplama dahil edilen üretim sayısı" />
        </div>

        {/* Araç çubuğu: filtreler */}
        <div style={{ ...cardStyle, padding: spacing.base, marginBottom: spacing.lg }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: spacing.md, alignItems: 'center' }}>
            <select value={lokasyonFiltre} onChange={(e) => setLokasyonFiltre(e.target.value)}
              style={{ ...inputStyle, width: 'auto', flex: '0 0 auto', cursor: 'pointer' }}>
              <option value="">Tüm lokasyonlar</option>
              {lokasyonlar.map(l => <option key={l.id} value={l.id}>{l.ad}</option>)}
            </select>

            <div style={{ display: 'inline-flex', alignItems: 'center', gap: spacing.xs }}>
              <input type="date" value={tarihBaslangicFiltre} onChange={(e) => setTarihBaslangicFiltre(e.target.value)}
                title="Başlangıç tarihi"
                style={{ ...inputStyle, width: 'auto', flex: '0 0 auto', cursor: 'pointer' }} />
              <span style={{ color: colors.textSecondary, fontSize: '13px' }}>–</span>
              <input type="date" value={tarihBitisFiltre} onChange={(e) => setTarihBitisFiltre(e.target.value)}
                title="Bitiş tarihi"
                style={{ ...inputStyle, width: 'auto', flex: '0 0 auto', cursor: 'pointer' }} />
            </div>

            <label style={{ display: 'inline-flex', alignItems: 'center', gap: spacing.xs, fontSize: '13px', color: colors.textSecondary, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={sadeceTamamlanan === '1'}
                onChange={(e) => setSadeceTamamlanan(e.target.checked ? '1' : '0')}
              />
              Sadece tamamlanan üretimler
            </label>

            {filtreAktif && (
              <button onClick={clearFiltreler} style={{ ...ghostBtn, padding: '8px 12px' }}>Temizle</button>
            )}
          </div>
        </div>

        {/* Özet tablosu */}
        <div style={{ ...cardStyle, overflow: 'hidden' }}>
          {loading ? (
            <div style={{ padding: spacing['3xl'], textAlign: 'center', color: colors.textSecondary }}>Yükleniyor…</div>
          ) : ozet.length === 0 ? (
            <div style={{ padding: spacing['3xl'], textAlign: 'center', color: colors.textTertiary }}>
              Seçilen kriterlere uyan üretim kaydı bulunamadı.
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: colors.backgroundAlt, color: colors.textSecondary, fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                    <th style={{ padding: '10px 12px', textAlign: 'left' }}>Ürün / Reçete</th>
                    <th style={{ padding: '10px 12px', textAlign: 'right' }}>Toplam Miktar</th>
                    <th style={{ padding: '10px 12px', textAlign: 'center' }}>Birim</th>
                    <th style={{ padding: '10px 12px', textAlign: 'right' }}>Kayıt Sayısı</th>
                  </tr>
                </thead>
                <tbody>
                  {ozet.map((g, idx) => (
                    <tr key={idx} style={{ borderTop: `1px solid ${colors.borderLight}` }}>
                      <td style={{ padding: '10px 12px', fontWeight: 600, color: colors.textPrimary }}>{g.ad}</td>
                      <td style={{ padding: '10px 12px', textAlign: 'right', color: colors.textPrimary }}>{miktarFmt(g.toplamMiktar)}</td>
                      <td style={{ padding: '10px 12px', textAlign: 'center', color: colors.textSecondary }}>{g.birim}</td>
                      <td style={{ padding: '10px 12px', textAlign: 'right', color: colors.textSecondary }}>{g.kayitSayisi}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UretimOzetPage;
